/**
 * InfiNet Wireless + Proxim Wireless PtP radio-antenna catalog.
 *
 * Two enterprise PtP brands that dominate outside the Ubiquiti /
 * Cambium core. InfiNet is the European / Russian / Middle-Eastern
 * incumbent (R5000 + Quanta + InfiLink lines, SDR technology,
 * proprietary scheduling). Proxim Wireless owns the North-American
 * Tsunami line (QB-8200 / QB-825) with WORP MAC for non-line-of-
 * sight backhauls.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const INFINET_PROXIM_RADIO_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // InfiNet Wireless — carrier-WISP / EU / RU / ME
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://infinetwireless.com/products/quanta-5
  // SRC: https://infinetwireless.com/products/quanta-5/specifications
  // SRC: https://www.abptech.com/infinet-wireless-introducing-new-sdr-technology-quanta-5
  //      Quanta 5: 5 + 6 GHz dual-band PtP backhaul radio, max 650
  //      Mbps gross throughput / 450 Mbps net @ 40 MHz channel,
  //      packet processing 800 000 pps. SDR (Software-Defined Radio)
  //      tech with proprietary scheduling for backhaul of Wi-Fi APs
  //      + 4G/LTE base stations + CCTV / video-surveillance links.
  //      Integrated antenna with dual polarisation.
  {
    id: 'infinet-quanta-5',
    manufacturer: 'InfiNet',
    model: 'Quanta 5',
    category: 'radio-antenna',
    tagline:
      'Quanta 5 — 5 + 6 GHz SDR PtP backhaul, 650 Mbps gross, 800 k pps, dual-pol',
    verified: true,
    datasheetUrl: 'https://infinetwireless.com/products/quanta-5',
    specs: {
      band: 'multi-band', // 5 GHz + 6 GHz
      rangeM: 40000, // typical long-range PtP for this gain class
      gainDbi: 23, // integrated antenna typical for Quanta-class
      beamWidthDeg: 9, // typical for 23 dBi panel at 5 GHz
      throughputMbps: 650,
      standard: 'proprietary', // InfiNet SDR + proprietary scheduling
      mimo: '2×2',
      ipRating: 'IP66',
      operatingTempC: '-40..+60',
    },
  },

  // SRC: https://infinetwireless.com/products/infilink-2x2/r5000-smn-23-dbi
  // SRC: https://infinetwireless.com/products/infiman-2x2/r5000-smn-21-dbi-man
  // SRC: https://www.abptech.com/sites/abptech/files/downloads/InfiLINK_2x2_datasheet.pdf
  //      R5000-Smn (23 dBi): point-to-point backhaul radio in the
  //      InfiLINK 2×2 family, 5 GHz, integrated 23 dBi dual-polarisation
  //      antenna, up to 180 Mbps net throughput. Lighter / lower-tier
  //      sibling to the Quanta series — used where the link budget
  //      doesn't demand Quanta-class throughput but the deployment
  //      still needs InfiNet's proprietary TDMA MAC + scheduling.
  {
    id: 'infinet-r5000-smn-23dbi',
    manufacturer: 'InfiNet',
    model: 'R5000-Smn (InfiLINK 2×2, 23 dBi)',
    category: 'radio-antenna',
    tagline:
      'R5000-Smn — 5 GHz InfiLINK 2×2 PtP, 23 dBi integrated dual-pol, 180 Mbps net',
    verified: true,
    datasheetUrl: 'https://infinetwireless.com/products/infilink-2x2/r5000-smn-23-dbi',
    specs: {
      band: '5 GHz',
      rangeM: 30000, // 23 dBi @ 5 GHz typical LoS
      gainDbi: 23,
      beamWidthDeg: 9,
      throughputMbps: 180,
      standard: 'proprietary', // InfiNet TDMA MAC
      mimo: '2×2',
      ipRating: 'IP66',
      operatingTempC: '-40..+60',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Proxim Wireless — Tsunami QuickBridge (NA carrier PtP)
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.anixter.com/content/dam/Suppliers/Proxim/Documents/PtP-QB-8200-Tsunami-QB-8200-G-datasheet-US.pdf
  // SRC: https://www.vsatplus.com/products/tsunami-qb-8200-link-300-mbps-mimo-3x3-type-n-connectors-two-qb-8200-epa-wd
  //      Tsunami QB-8200: high-power non-LoS 4G PtP wireless backhaul,
  //      300 Mbps data rate, MIMO 3×3 OFDM radio, 25.8 dBm high-power
  //      TX, Type-N connectors (connectorized — BYO antenna), GPS
  //      synchronisation for dense PtMP cells, Proxim's proprietary
  //      Wireless Outdoor Routing Protocol (WORP) MAC for backhaul
  //      of wireless video-surveillance + carrier traffic.
  {
    id: 'proxim-tsunami-qb-8200',
    manufacturer: 'Proxim',
    model: 'Tsunami QB-8200',
    category: 'radio-antenna',
    tagline:
      'Tsunami QB-8200 — 5 GHz non-LoS PtP, 300 Mbps, MIMO 3×3, 25.8 dBm TX, WORP MAC',
    verified: true,
    datasheetUrl:
      'https://www.anixter.com/content/dam/Suppliers/Proxim/Documents/PtP-QB-8200-Tsunami-QB-8200-G-datasheet-US.pdf',
    specs: {
      band: '5 GHz',
      rangeM: 25000, // typical NLoS-tolerant PtP with this TX power class
      gainDbi: 0, // connectorized — BYO antenna, gain depends on chosen antenna
      throughputMbps: 300,
      standard: 'proprietary', // WORP MAC, not vanilla 802.11
      mimo: '3×3',
      ipRating: 'IP67',
      operatingTempC: '-40..+60',
    },
  },
];
